import type { AiCoach_ConfigurationInput as CoachConfigType } from '__generated__/graphql-types';

// Collapsed-only pull-tab / grab button, sized as a comfortable touch target.
// The chat container height = sheet height − STRIP_HEIGHT, and the detent heights
// below add STRIP_HEIGHT on top of the intended conversation area, so the strip can
// grow without shrinking the conversation (which would trip the coach-chat
// container-query breakpoints in Conversation.tsx).
export const STRIP_HEIGHT = 44;
// Fallback page-header height until the real header is measured for the lower detents.
// Full-height mode intentionally covers the mobile header.
export const HEADER_OFFSET_FALLBACK = 65;
export const PAGE_HEADER_SELECTOR = '[data-testid="page-header-wrapper"]';
// Fallback heights used before the viewport is measured (SSR / first paint).
export const DEFAULT_MAX_HEIGHT = 640;
// ~436px conversation + STRIP_HEIGHT grab strip.
export const DEFAULT_EXPANDED_HEIGHT = 480;
// Minimum drag distance (px) to count as a drag rather than a tap.
export const MIN_DRAG_DISTANCE = 5;
// The mobile page header/search overlay sits at 3000/3001, so keep the coach sheet above it.
export const BOTTOM_SHEET_Z_INDEX = 3002;
export const BOTTOM_SHEET_HEIGHT_VAR = '--coach-bottom-sheet-height' as const;
// Lowest visible detent: a slim peek sized to the composer only (the chat list and
// header are hidden via a container query at this height — see CoachSidebar).
// ~116px conversation peek + STRIP_HEIGHT grab strip.
export const COLLAPSED_HEIGHT = 160;
// Releasing a downward drag this far below COLLAPSED_HEIGHT dismisses the sheet entirely
// (the parent unmounts it; it reopens from the search bar). Avoids accidental close on a
// small nudge.
export const CLOSE_DRAG_THRESHOLD = 56;

// Footer-carve "active" signal (any positive value); actual rendered width is
// COACH_PANEL_WIDTH_CSS below. Matches XDP's XDP_CHAT_PANEL_WIDTH at full size.
export const COACH_PANEL_WIDTH = 416;

// Responsive width: shrinks below 1222px viewport (416 panel + 788 results) with a
// 320px floor. Pure CSS (clamp) keeps panel, results column, and footer carve in
// sync without measurement.
export const COACH_PANEL_WIDTH_CSS = 'clamp(320px, calc(100vw - 788px), 416px)';

export const coachConfig: CoachConfigType = {
  coachModeId: 'SEARCH_DISCOVERY_MVP_MODE_V1',
  coachActionId: 'TYPED_MESSAGE',
};

export const coachInitialSearchResultsConfig: CoachConfigType = {
  coachModeId: 'SEARCH_TWO_PANEL',
  coachActionId: 'INITIAL_SEARCH_RESULTS',
};

export const coachSearchRouterConfig: CoachConfigType = {
  coachModeId: 'SEARCH_TWO_PANEL',
  coachActionId: 'SEARCH_ROUTER',
  includeStructuredOutputInChatHistory: false,
};

export const coachUpdatedSearchResultsConfig: CoachConfigType = {
  coachModeId: 'SEARCH_TWO_PANEL',
  coachActionId: 'SEARCH_RESULTS',
};

export const coachFetchContentConfig: CoachConfigType = {
  coachModeId: 'SEARCH_TWO_PANEL',
  coachActionId: 'FETCH_CONTENT',
};

export const coachHelpAndSupportConfig: CoachConfigType = {
  coachModeId: 'AI_SEARCH_HELP_AND_SUPPORT',
  coachActionId: 'TYPED_MESSAGE',
};

export const coachExploreCareerSkillConfig: CoachConfigType = {
  coachModeId: 'DISCOVERY_EXPLORE_CAREER_SKILL',
  coachActionId: 'TYPED_MESSAGE',
};

export const coachAboutCourseConfig: CoachConfigType = {
  coachModeId: 'DISCOVERY_ABOUT_COURSE',
  coachActionId: 'TYPED_MESSAGE',
};

export const coachContentRetrievalConfig: CoachConfigType = {
  coachModeId: 'DISCOVERY_FIND_CONTENT',
  coachActionId: 'CONTENT_RETRIEVAL',
};

export const coachConversationalDiscoveryConfig: CoachConfigType = {
  coachModeId: 'CONVERSATIONAL_DISCOVERY',
  coachActionId: 'TYPED_MESSAGE',
};

export const coachXdpFocusedConfig: CoachConfigType = {
  coachModeId: 'XDP_CAREER_FIT_ASSISTANT_V3',
  coachActionId: 'TYPED_MESSAGE',
};

export const coachBestForJustificationConfig: CoachConfigType = {
  coachModeId: 'SEARCH_TWO_PANEL',
  coachActionId: 'BEST_FOR_JUSTIFICATION',
  includeStructuredOutputInChatHistory: false,
};

export const coachCompareConfig: CoachConfigType = {
  coachModeId: 'SEARCH_TWO_PANEL',
  coachActionId: 'CONTENT_COMPARE',
};

export const coachAdditionalContext = {
  contextKey: 'additionalContext.roleData',
  contextValue: 'New Learner',
};
